// components/RichTextEditor.jsx
import React, { useCallback } from "react";
import { Remirror, ThemeProvider, useRemirror, EditorComponent, useActive, useChainedCommands, useEditorView } from "@remirror/react";
import {
    BoldExtension,
    ItalicExtension,
    UnderlineExtension,
    BulletListExtension,
    ListItemExtension,
} from "remirror/extensions";
import { Fragment } from "@remirror/pm/model";
import { FaBold, FaItalic, FaUnderline, FaListUl } from 'react-icons/fa6';
import { sanitizeHtml } from "../utils/sanitizeHtml";

import "remirror/styles/all.css";

const Toolbar = () => {
    const active = useActive();
    const commands = useChainedCommands();

    const btnStyle = (isActive) => ({
        width: "36px",
        height: "32px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: isActive ? "#e9ecef" : "#fff",
        border: "1px solid #ced4da",
        borderRadius: "4px",
        color: "#333", 
        cursor: "pointer",
        padding: "0",
        transition: "all 0.2s"
    });

    return (
        <div style={{ 
            borderBottom: "1px solid #dee2e6", 
            padding: "8px 12px", 
            display: "flex", 
            flexWrap: "wrap",
            gap: "8px", 
            background: "#f8f9fa",
            borderTopLeftRadius: "6px",
            borderTopRightRadius: "6px",
            width: "100%",
            boxSizing: "border-box"
        }}>
            <button type="button" onClick={(e) => { e.preventDefault(); commands.toggleBold().focus().run(); }} style={btnStyle(active.bold())} title="Bold"><FaBold size={12} /></button>
            <button type="button" onClick={(e) => { e.preventDefault(); commands.toggleItalic().focus().run(); }} style={btnStyle(active.italic())} title="Italic"><FaItalic size={12} /></button>
            <button type="button" onClick={(e) => { e.preventDefault(); commands.toggleUnderline().focus().run(); }} style={btnStyle(active.underline())} title="Underline"><FaUnderline size={12} /></button>
            <button type="button" onClick={(e) => { e.preventDefault(); commands.toggleBulletList().focus().run(); }} style={btnStyle(active.bulletList())} title="Bullet List"><FaListUl size={12} /></button>
        </div>
    );
};

const EditorWithPasteHandler = () => {
    const view = useEditorView();

    const handlePaste = (event) => {
        const plainText = event.clipboardData?.getData("text/plain");
        if (!plainText || !/\r?\n/.test(plainText)) return;

        const lines = plainText
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter(Boolean);

        if (lines.length <= 1) return;

        event.preventDefault();
        event.stopPropagation();

        const { state } = view;
        const { $from, $to } = state.selection;
        const { bulletList, listItem, paragraph } = state.schema.nodes;
        const listItemDepth = Array.from({ length: $from.depth + 1 }, (_, depth) => depth)
            .reverse()
            .find((depth) => $from.node(depth).type === listItem);

        const paragraphNode = (text) => paragraph.create(null, text ? state.schema.text(text) : undefined);
        const listItemNode = (text) => listItem.create(null, paragraphNode(text));

        if (listItemDepth !== undefined) {
            const currentText = $from.parent.textContent;
            const canPreserveCurrentParagraph = $from.sameParent($to) && $from.parent.type === paragraph;
            const prefix = canPreserveCurrentParagraph ? currentText.slice(0, $from.parentOffset) : "";
            const suffix = canPreserveCurrentParagraph ? currentText.slice($to.parentOffset) : "";
            const pastedLines = [...lines];
            pastedLines[0] = `${prefix}${pastedLines[0]}`;
            pastedLines[pastedLines.length - 1] = `${pastedLines[pastedLines.length - 1]}${suffix}`;

            try {
                const transaction = state.tr.replaceWith(
                    $from.before(listItemDepth),
                    $from.after(listItemDepth),
                    Fragment.fromArray(pastedLines.map(listItemNode))
                );
                view.dispatch(transaction.scrollIntoView());
                view.focus();
                return;
            } catch (error) {
                console.error("Failed to paste multiline list items:", error);
            }
        }

        const list = bulletList.create(null, lines.map(listItemNode));
        view.dispatch(state.tr.replaceSelectionWith(list).scrollIntoView());
        view.focus();
    };

    return (
        <div onPasteCapture={handlePaste}>
            <EditorComponent />
        </div>
    );
};

const RichTextEditor = ({ html, onChange, style }) => {
    const extensions = useCallback(() => [
            new BoldExtension(),
            new ItalicExtension(),
            new UnderlineExtension(),
            new BulletListExtension(),
            new ListItemExtension(),
        ], []);

    const { manager, state } = useRemirror({
        extensions,
        content: sanitizeHtml(html || ""),
        stringHandler: "html",
    });

    return (
            <div
                className="rte-outer-container"
                style={{
                    background: "#ffffff",
                    border: "1px solid #ced4da",
                    borderRadius: "6px",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    boxSizing: "border-box",
                    ...style 
                }}
            >
            <ThemeProvider>
                <style dangerouslySetInnerHTML={{ __html: `
                    .rte-outer-container .remirror-theme {
                        width: 100% !important;
                        max-width: 100% !important;
                    }
                    .rte-outer-container .remirror-editor-wrapper {
                        width: 100% !important;
                        max-width: 100% !important;
                        padding: 0 !important;
                        display: block !important;
                    }
                    .rte-outer-container .ProseMirror {
                        width: 100% !important;
                        max-width: 100% !important;
                        min-height: 200px !important;
                        box-sizing: border-box !important;
                        padding: 15px !important;
                        outline: none !important;
                        color: #333 !important;
                        word-break: break-word !important;
                    }
                `}} />
                <Remirror
                    manager={manager}
                    initialContent={state}
                    onChange={({ helpers }) => {
                        const newHtml = sanitizeHtml(helpers.getHTML());
                        onChange(newHtml);
                    }}
                >
                    <Toolbar />
                    <EditorWithPasteHandler />
                </Remirror>
            </ThemeProvider>
            </div>
    );
};

export default RichTextEditor;
